/* eslint-disable no-unknown/no-unknown */
// @services/clients/DocumentAssistantSupplyApi.ts
import * as E from "fp-ts/Either";
import * as t from "io-ts";
import { PathReporter } from "io-ts/PathReporter";

import type { OptionTs } from "@ps-bridge/interop";
import * as Option from "@utils/Option";

export const DocumentAssistantSupplyContract = {
    contractId: "document.assistant.supply.v1",
} as const;

export const DocumentAssistantSupplyRoutes = {
    manualUpload: "/v1/document/assistant/supply/manual-upload",
    emailAttachment: "/v1/document/assistant/supply/email-attachment",
    status: (supplyId: SupplyId): string =>
        `/v1/document/assistant/supply/status/${encodeURIComponent(supplyId)}`,
} as const;

export type SupplyJsonPayload = string | number | boolean | object | readonly SupplyJsonPayload[];

export type SupplyId = string & {
    readonly __brandSupplyId: "SupplyId";
};

export type SupplyBatchId = string & {
    readonly __brandSupplyBatchId: "SupplyBatchId";
};

export type SupplyDisplayName = string & {
    readonly __brandSupplyDisplayName: "SupplyDisplayName";
};

export type SupplyErrorMessage = string & {
    readonly __brandSupplyErrorMessage: "SupplyErrorMessage";
};

export type SupplyByteDigest = string & {
    readonly __brandSupplyByteDigest: "SupplyByteDigest";
};

export type SupplySourceId = string & {
    readonly __brandSupplySourceId: "SupplySourceId";
};

export type SupplySourceHandle = string & {
    readonly __brandSupplySourceHandle: "SupplySourceHandle";
};

export type SupplyMessageHandle = string & {
    readonly __brandSupplyMessageHandle: "SupplyMessageHandle";
};

export type SupplyAttachmentName = string & {
    readonly __brandSupplyAttachmentName: "SupplyAttachmentName";
};

export type SupplyListingId = string & {
    readonly __brandSupplyListingId: "SupplyListingId";
};

export type SupplyTraceId = string & {
    readonly __brandSupplyTraceId: "SupplyTraceId";
};

export type SupplyRequestId = string & {
    readonly __brandSupplyRequestId: "SupplyRequestId";
};

export type SupplyReceivedAtIso = string & {
    readonly __brandSupplyReceivedAtIso: "SupplyReceivedAtIso";
};

export type SupplySourceRevisionValue = number & {
    readonly __brandSupplySourceRevisionValue: "SupplySourceRevisionValue";
};

export type SupplyClassification =
    | "cimDraft"
    | "sdeSpreadsheet"
    | "dealFile"
    | "emailAttachment"
    | "transcript"
    | "unknown";

export type SupplyIndexingDecision =
    | "ready"
    | "holdForClassification"
    | "holdForListingAssociation"
    | "holdForManualReview"
    | "notIndexable";

export type SupplyRetrievalEligibility = "eligible" | "ineligible" | "pending";

export type SupplyUploadStatus =
    | "stored"
    | "registeredSource"
    | "classified"
    | "associatedToListing"
    | "pendingPartyAssociation"
    | "indexReady"
    | "retrievalReady"
    | "failed";

export type SupplyProvenance = "userUpload" | "emailAttachment" | "fileStorage" | "spreadsheet";

export type SupplyRefusalCode = "invalidRequest" | "notFound" | "conflict" | "serviceUnavailable";

export type SupplyErrorCode = SupplyRefusalCode | "networkError" | "responseDecodeError";

export type SupplyRequestTrace = {
    readonly traceId: SupplyTraceId;
    readonly requestId: SupplyRequestId;
};

export type SupplySourceRevision = {
    readonly sourceId: SupplySourceId;
    readonly revision: SupplySourceRevisionValue;
};

export type SupplyManualUploadRequest = {
    readonly supplyId: SupplyId;
    readonly batchId: SupplyBatchId;
    readonly displayName: SupplyDisplayName;
    readonly byteDigest: SupplyByteDigest;
    readonly sourceId: SupplySourceId;
    readonly sourceHandle: SupplySourceHandle;
    readonly listingId: OptionTs<SupplyListingId>;
};

export type SupplyEmailAttachmentRequest = {
    readonly supplyId: SupplyId;
    readonly batchId: SupplyBatchId;
    readonly displayName: SupplyDisplayName;
    readonly byteDigest: SupplyByteDigest;
    readonly sourceId: SupplySourceId;
    readonly messageHandle: SupplyMessageHandle;
    readonly attachmentName: SupplyAttachmentName;
    readonly receivedAt: SupplyReceivedAtIso;
    readonly listingId: OptionTs<SupplyListingId>;
};

export type SupplyAcceptedResponse = {
    readonly supplyId: SupplyId;
    readonly batchId: SupplyBatchId;
    readonly displayName: SupplyDisplayName;
    readonly sourceId: SupplySourceId;
    readonly sourceRevision: SupplySourceRevision;
    readonly classification: SupplyClassification;
    readonly indexing: SupplyIndexingDecision;
    readonly retrieval: SupplyRetrievalEligibility;
    readonly uploadStatus: SupplyUploadStatus;
    readonly listingId: OptionTs<SupplyListingId>;
    readonly provenance: SupplyProvenance;
    readonly trace: SupplyRequestTrace;
};

export type SupplyStatusResponse = {
    readonly supplyId: SupplyId;
    readonly displayName: SupplyDisplayName;
    readonly sourceId: OptionTs<SupplySourceId>;
    readonly classification: SupplyClassification;
    readonly indexing: SupplyIndexingDecision;
    readonly retrieval: SupplyRetrievalEligibility;
    readonly uploadStatus: SupplyUploadStatus;
    readonly listingId: OptionTs<SupplyListingId>;
    readonly provenance: SupplyProvenance;
    readonly trace: SupplyRequestTrace;
};

export type SupplyRefusal = {
    readonly error: SupplyRefusalCode;
    readonly message: SupplyErrorMessage;
    readonly trace: SupplyRequestTrace;
};

export type SupplyError = {
    readonly error: SupplyErrorCode;
    readonly message: SupplyErrorMessage;
    readonly trace: OptionTs<SupplyRequestTrace>;
};

export type SupplyApiDecodeResult<T> =
    | {
          readonly type: "ok";
          readonly value: T;
      }
    | {
          readonly type: "error";
          readonly message: string;
      };

const toStringBrand = <T extends string>(value: string): T => value as T;
const toNumberBrand = <T extends number>(value: number): T => value as T;

const nonEmpty = (value: string): string => value.trim();

const unsafeSupplyId = (value: string): SupplyId => toStringBrand(nonEmpty(value));
const unsafeSupplyBatchId = (value: string): SupplyBatchId => toStringBrand(nonEmpty(value));
const unsafeSupplyDisplayName = (value: string): SupplyDisplayName => toStringBrand(nonEmpty(value));
const unsafeSupplyErrorMessage = (value: string): SupplyErrorMessage => toStringBrand(nonEmpty(value));
const unsafeSupplyByteDigest = (value: string): SupplyByteDigest => toStringBrand(nonEmpty(value));
const unsafeSupplySourceId = (value: string): SupplySourceId => toStringBrand(nonEmpty(value));
const unsafeSupplySourceHandle = (value: string): SupplySourceHandle => toStringBrand(nonEmpty(value));
const unsafeSupplyMessageHandle = (value: string): SupplyMessageHandle => toStringBrand(nonEmpty(value));
const unsafeSupplyAttachmentName = (value: string): SupplyAttachmentName => toStringBrand(nonEmpty(value));
const unsafeSupplyListingId = (value: string): SupplyListingId => toStringBrand(nonEmpty(value));
const unsafeSupplyTraceId = (value: string): SupplyTraceId => toStringBrand(nonEmpty(value));
const unsafeSupplyRequestId = (value: string): SupplyRequestId => toStringBrand(nonEmpty(value));
const unsafeSupplyReceivedAtIso = (value: string): SupplyReceivedAtIso => toStringBrand(nonEmpty(value));
const unsafeSupplySourceRevisionValue = (value: number): SupplySourceRevisionValue => toNumberBrand(value);

const jsonNull = (): SupplyJsonPayload => JSON.parse("null") as SupplyJsonPayload;

const optionCodec = <A>(codec: t.Decoder<unknown, A>): t.Type<OptionTs<A>, unknown, unknown> =>
    new t.Type<OptionTs<A>, unknown, unknown>(
        "Option",
        (value): value is OptionTs<A> => typeof value === "object",
        (input, context) => {
            if (input === jsonNull() || typeof input === "undefined") {
                return t.success(Option.none<A>());
            }
            const decoded = codec.decode(input);
            if (E.isRight(decoded)) {
                return t.success(Option.some(decoded.right));
            }
            return t.failure(input, context, PathReporter.report(decoded).join("; "));
        },
        (value) => Option.fold(value, jsonNull, (inner) => inner as SupplyJsonPayload),
    );

const nonEmptyStringCodec = <A extends string>(name: string, makeValue: (value: string) => A): t.Type<A, string, unknown> =>
    new t.Type<A, string, unknown>(
        name,
        (value): value is A => typeof value === "string" && value.trim().length > 0,
        (input, context) => {
            if (typeof input !== "string" || input.trim().length === 0) {
                return t.failure(input, context, `${name} must be a non-empty string`);
            }
            return t.success(makeValue(input));
        },
        (value) => value,
    );

const nonNegativeIntegerCodec = <A extends number>(name: string, makeValue: (value: number) => A): t.Type<A, number, unknown> =>
    new t.Type<A, number, unknown>(
        name,
        (value): value is A => typeof value === "number" && Number.isInteger(value) && value >= 0,
        (input, context) => {
            if (typeof input !== "number" || !Number.isInteger(input) || input < 0) {
                return t.failure(input, context, `${name} must be a non-negative integer`);
            }
            return t.success(makeValue(input));
        },
        (value) => value,
    );

const isoDateStringCodec: t.Type<SupplyReceivedAtIso, string, unknown> = new t.Type<SupplyReceivedAtIso, string, unknown>(
    "SupplyReceivedAtIso",
    (value): value is SupplyReceivedAtIso => typeof value === "string" && !Number.isNaN(Date.parse(value)),
    (input, context) => {
        if (typeof input !== "string" || Number.isNaN(Date.parse(input))) {
            return t.failure(input, context, "SupplyReceivedAtIso must be an ISO-compatible timestamp");
        }
        return t.success(unsafeSupplyReceivedAtIso(input));
    },
    (value) => value,
);

const supplyIdCodec = nonEmptyStringCodec("SupplyId", unsafeSupplyId);
const supplyBatchIdCodec = nonEmptyStringCodec("SupplyBatchId", unsafeSupplyBatchId);
const supplyDisplayNameCodec = nonEmptyStringCodec("SupplyDisplayName", unsafeSupplyDisplayName);
const supplyErrorMessageCodec = nonEmptyStringCodec("SupplyErrorMessage", unsafeSupplyErrorMessage);
const supplyByteDigestCodec = nonEmptyStringCodec("SupplyByteDigest", unsafeSupplyByteDigest);
const supplySourceIdCodec = nonEmptyStringCodec("SupplySourceId", unsafeSupplySourceId);
const supplySourceHandleCodec = nonEmptyStringCodec("SupplySourceHandle", unsafeSupplySourceHandle);
const supplyMessageHandleCodec = nonEmptyStringCodec("SupplyMessageHandle", unsafeSupplyMessageHandle);
const supplyAttachmentNameCodec = nonEmptyStringCodec("SupplyAttachmentName", unsafeSupplyAttachmentName);
const supplyListingIdCodec = nonEmptyStringCodec("SupplyListingId", unsafeSupplyListingId);
const supplyTraceIdCodec = nonEmptyStringCodec("SupplyTraceId", unsafeSupplyTraceId);
const supplyRequestIdCodec = nonEmptyStringCodec("SupplyRequestId", unsafeSupplyRequestId);
const supplySourceRevisionValueCodec = nonNegativeIntegerCodec(
    "SupplySourceRevisionValue",
    unsafeSupplySourceRevisionValue,
);

const supplyClassificationCodec = t.keyof({
    cimDraft: 0,
    sdeSpreadsheet: 0,
    dealFile: 0,
    emailAttachment: 0,
    transcript: 0,
    unknown: 0,
});

const supplyIndexingDecisionCodec = t.keyof({
    ready: 0,
    holdForClassification: 0,
    holdForListingAssociation: 0,
    holdForManualReview: 0,
    notIndexable: 0,
});

const supplyRetrievalEligibilityCodec = t.keyof({
    eligible: 0,
    ineligible: 0,
    pending: 0,
});

const supplyUploadStatusCodec = t.keyof({
    stored: 0,
    registeredSource: 0,
    classified: 0,
    associatedToListing: 0,
    pendingPartyAssociation: 0,
    indexReady: 0,
    retrievalReady: 0,
    failed: 0,
});

const supplyProvenanceCodec = t.keyof({
    userUpload: 0,
    emailAttachment: 0,
    fileStorage: 0,
    spreadsheet: 0,
});

const supplyRefusalCodeCodec = t.keyof({
    invalidRequest: 0,
    notFound: 0,
    conflict: 0,
    serviceUnavailable: 0,
});

export const supplyRequestTraceCodec = t.type({
    traceId: supplyTraceIdCodec,
    requestId: supplyRequestIdCodec,
});

export const supplySourceRevisionCodec = t.type({
    sourceId: supplySourceIdCodec,
    revision: supplySourceRevisionValueCodec,
});

export const supplyManualUploadRequestCodec = t.type({
    supplyId: supplyIdCodec,
    batchId: supplyBatchIdCodec,
    displayName: supplyDisplayNameCodec,
    byteDigest: supplyByteDigestCodec,
    sourceId: supplySourceIdCodec,
    sourceHandle: supplySourceHandleCodec,
    listingId: optionCodec(supplyListingIdCodec),
});

export const supplyEmailAttachmentRequestCodec = t.type({
    supplyId: supplyIdCodec,
    batchId: supplyBatchIdCodec,
    displayName: supplyDisplayNameCodec,
    byteDigest: supplyByteDigestCodec,
    sourceId: supplySourceIdCodec,
    messageHandle: supplyMessageHandleCodec,
    attachmentName: supplyAttachmentNameCodec,
    receivedAt: isoDateStringCodec,
    listingId: optionCodec(supplyListingIdCodec),
});

export const supplyAcceptedResponseCodec = t.type({
    supplyId: supplyIdCodec,
    batchId: supplyBatchIdCodec,
    displayName: supplyDisplayNameCodec,
    sourceId: supplySourceIdCodec,
    sourceRevision: supplySourceRevisionCodec,
    classification: supplyClassificationCodec,
    indexing: supplyIndexingDecisionCodec,
    retrieval: supplyRetrievalEligibilityCodec,
    uploadStatus: supplyUploadStatusCodec,
    listingId: optionCodec(supplyListingIdCodec),
    provenance: supplyProvenanceCodec,
    trace: supplyRequestTraceCodec,
});

export const supplyStatusResponseCodec = t.type({
    supplyId: supplyIdCodec,
    displayName: supplyDisplayNameCodec,
    sourceId: optionCodec(supplySourceIdCodec),
    classification: supplyClassificationCodec,
    indexing: supplyIndexingDecisionCodec,
    retrieval: supplyRetrievalEligibilityCodec,
    uploadStatus: supplyUploadStatusCodec,
    listingId: optionCodec(supplyListingIdCodec),
    provenance: supplyProvenanceCodec,
    trace: supplyRequestTraceCodec,
});

export const supplyRefusalCodec = t.type({
    error: supplyRefusalCodeCodec,
    message: supplyErrorMessageCodec,
    trace: supplyRequestTraceCodec,
});

const decodeWith = <T>(codec: t.Decoder<unknown, T>, payload: unknown): SupplyApiDecodeResult<T> => {
    const decoded = codec.decode(payload);
    if (E.isRight(decoded)) {
        return { type: "ok", value: decoded.right };
    }
    return { type: "error", message: PathReporter.report(decoded).join("; ") };
};

export const decodeSupplyManualUploadRequest = (payload: unknown): SupplyApiDecodeResult<SupplyManualUploadRequest> =>
    decodeWith(supplyManualUploadRequestCodec, payload);

export const decodeSupplyEmailAttachmentRequest = (payload: unknown): SupplyApiDecodeResult<SupplyEmailAttachmentRequest> =>
    decodeWith(supplyEmailAttachmentRequestCodec, payload);

export const decodeSupplyAcceptedResponse = (payload: unknown): SupplyApiDecodeResult<SupplyAcceptedResponse> =>
    decodeWith(supplyAcceptedResponseCodec, payload);

export const decodeSupplyStatusResponse = (payload: unknown): SupplyApiDecodeResult<SupplyStatusResponse> =>
    decodeWith(supplyStatusResponseCodec, payload);

export const decodeSupplyRefusal = (payload: unknown): SupplyApiDecodeResult<SupplyRefusal> =>
    decodeWith(supplyRefusalCodec, payload);

export const makeSupplyId = (value: string): SupplyApiDecodeResult<SupplyId> => decodeWith(supplyIdCodec, value);
export const makeSupplyBatchId = (value: string): SupplyApiDecodeResult<SupplyBatchId> => decodeWith(supplyBatchIdCodec, value);
export const makeSupplyDisplayName = (value: string): SupplyApiDecodeResult<SupplyDisplayName> =>
    decodeWith(supplyDisplayNameCodec, value);
export const makeSupplyErrorMessage = (value: string): SupplyApiDecodeResult<SupplyErrorMessage> =>
    decodeWith(supplyErrorMessageCodec, value);
export const makeSupplyByteDigest = (value: string): SupplyApiDecodeResult<SupplyByteDigest> =>
    decodeWith(supplyByteDigestCodec, value);
export const makeSupplySourceId = (value: string): SupplyApiDecodeResult<SupplySourceId> =>
    decodeWith(supplySourceIdCodec, value);
export const makeSupplySourceHandle = (value: string): SupplyApiDecodeResult<SupplySourceHandle> =>
    decodeWith(supplySourceHandleCodec, value);
export const makeSupplyMessageHandle = (value: string): SupplyApiDecodeResult<SupplyMessageHandle> =>
    decodeWith(supplyMessageHandleCodec, value);
export const makeSupplyAttachmentName = (value: string): SupplyApiDecodeResult<SupplyAttachmentName> =>
    decodeWith(supplyAttachmentNameCodec, value);
export const makeSupplyListingId = (value: string): SupplyApiDecodeResult<SupplyListingId> =>
    decodeWith(supplyListingIdCodec, value);
export const makeSupplyTraceId = (value: string): SupplyApiDecodeResult<SupplyTraceId> =>
    decodeWith(supplyTraceIdCodec, value);
export const makeSupplyRequestId = (value: string): SupplyApiDecodeResult<SupplyRequestId> =>
    decodeWith(supplyRequestIdCodec, value);
export const makeSupplyReceivedAtIso = (value: string): SupplyApiDecodeResult<SupplyReceivedAtIso> =>
    decodeWith(isoDateStringCodec, value);
export const makeSupplySourceRevisionValue = (value: number): SupplyApiDecodeResult<SupplySourceRevisionValue> =>
    decodeWith(supplySourceRevisionValueCodec, value);

export const encodeSupplyManualUploadRequest = (request: SupplyManualUploadRequest): SupplyJsonPayload => ({
    supplyId: request.supplyId,
    batchId: request.batchId,
    displayName: request.displayName,
    byteDigest: request.byteDigest,
    sourceId: request.sourceId,
    sourceHandle: request.sourceHandle,
    listingId: Option.fold(request.listingId, jsonNull, (listingId) => listingId),
});

export const encodeSupplyEmailAttachmentRequest = (request: SupplyEmailAttachmentRequest): SupplyJsonPayload => ({
    supplyId: request.supplyId,
    batchId: request.batchId,
    displayName: request.displayName,
    byteDigest: request.byteDigest,
    sourceId: request.sourceId,
    messageHandle: request.messageHandle,
    attachmentName: request.attachmentName,
    receivedAt: request.receivedAt,
    listingId: Option.fold(request.listingId, jsonNull, (listingId) => listingId),
});

export const supplyLocalError = (error: SupplyErrorCode, message: string): SupplyError => {
    const decodedMessage = makeSupplyErrorMessage(message);
    return {
        error,
        message: decodedMessage.type === "ok" ? decodedMessage.value : unsafeSupplyErrorMessage("Document supply error"),
        trace: Option.none<SupplyRequestTrace>(),
    };
};

export const supplyErrorFromRefusal = (refusal: SupplyRefusal): SupplyError => ({
    error: refusal.error,
    message: refusal.message,
    trace: Option.some(refusal.trace),
});
